// Phase 8.6 follow-up — the personnel create/edit form Content component
// (ADR-014 §2). Mirrors leave-form-content.tsx: a pure, shell-agnostic Content
// component built on the shared <ResourceForm> (zod-style validator port — no
// hard zod dependency in the app; a hand-rolled safeParse satisfies the
// contract). The SAME component renders both the create and edit surfaces; the
// host page (employee-form-page.tsx) wires the POST/PATCH mutation.
//
// Fields v1 (simplicity-first): the 4 required (first/last name, work email,
// start_date) + job_title + employment_type (enum select). The manager FK is a
// picker over the existing employees list. The legal_entity FK joined in
// SEM-6755: `GET /v1/legal-entities` became company-aware, so its rows already
// carry only the caller's own grant and the picker cannot offer a company the
// write end will reject. The department picker (E10) reads the active
// `GET /v1/departments` list. work_location stays deferred: no ready
// option-source exists in the frontend for it yet.
//
// Phone, home address and emergency contacts are NOT fields of this form. They
// are a separate resource (`/v1/employees/:id/personal-info`, HR tier only)
// with its own audit row, edited from the person's card
// (features/employees/personal-info-card.tsx); folding them in here would make
// one submit two independent writes that can half-succeed. TCKN and the salary
// IBAN are payroll-owned (spec 69) and appear on no personnel form.
//
// All labels are i18n keys (ADR-006). The backend lands a created employee at
// lifecycle_state=pre_hire; lifecycle is not a form field.

import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import { Link as RouterLink } from "react-router";

import { EMPLOYMENT_TYPES } from "@novemai/hris-sdk";
import {
  Card,
  CardBody,
  PageHeader,
  ResourceForm,
  type ResourceField,
  type ResourceFieldOption,
  type SafeParseValidator,
} from "@novemai/components";



/** The validated employee draft this form produces. Optional fields are empty
 * strings when unset (the host drops empties before the POST/PATCH). */
export interface EmployeeDraft {
  readonly legal_first_name: string;
  readonly legal_last_name: string;
  readonly primary_work_email: string;
  readonly start_date: string;
  readonly job_title: string;
  readonly employment_type: string;
  readonly manager_id: string;
  readonly department_id: string;
  readonly legal_entity_id: string;
  /** Boyut slug'ı → değer (ADR-012). Boş değerler taşınmaz. */
  readonly org_dimension_values: Readonly<Record<string, string>>;
}

/**
 * Hand-rolled validator satisfying the @novemai/components SafeParseValidator
 * port (same convention as makeLeaveValidator). Returns localized message KEYS
 * the host already resolved via the `msg` map (error text stays i18n, ADR-006).
 */
function makeEmployeeValidator(
  msg: (key: string) => string,
  companyRequired: boolean,
  titleRequired: boolean,
  dimensions: readonly OrgDimensionField[],
): SafeParseValidator<EmployeeDraft> {
  const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
  // Conservative email shape — the backend's z.string().email() is the real
  // gate; this is the inline UX hint before the round-trip.
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return {
    safeParse(input: unknown) {
      const raw = (input ?? {}) as Record<string, unknown>;
      const issues: Array<{ path: string[]; message: string }> = [];

      const firstName = String(raw["legal_first_name"] ?? "").trim();
      if (firstName.length === 0) {
        issues.push({
          path: ["legal_first_name"],
          message: msg("people.form.error.first_name_required"),
        });
      }
      const lastName = String(raw["legal_last_name"] ?? "").trim();
      if (lastName.length === 0) {
        issues.push({
          path: ["legal_last_name"],
          message: msg("people.form.error.last_name_required"),
        });
      }
      const email = String(raw["primary_work_email"] ?? "").trim();
      if (!EMAIL.test(email)) {
        issues.push({
          path: ["primary_work_email"],
          message: msg("people.form.error.email_invalid"),
        });
      }
      const startDate = String(raw["start_date"] ?? "");
      if (!ISO_DATE.test(startDate)) {
        issues.push({
          path: ["start_date"],
          message: msg("people.form.error.start_invalid"),
        });
      }

      // Şirket YALNIZ seçici gösterildiğinde ve YALNIZ işe alımda zorunlu.
      // Kiracının hiç şirketi yoksa (ya da çağıranın hibesi boşsa) alan hiç
      // çizilmiyor; zorunluluğu koşulsuz yazmak o kiracıda formu kapatırdı.
      // Düzenlemede alan kilitli, kilitli bir <select> gövdeye hiç gitmez —
      // zorunluluk orada da dursaydı her düzenleme kaydedilemezdi (MC-19b).
      const legalEntityId = String(raw["legal_entity_id"] ?? "").trim();
      if (companyRequired && legalEntityId.length === 0) {
        issues.push({
          path: ["legal_entity_id"],
          message: msg("people.form.error.company_required"),
        });
      }

      // Ünvan YALNIZ işe alımda zorunlu (MC-19a). İşe alım birincil istihdam
      // kaydını açıyor ve o kaydın ünvanı boş olamaz. Düzenlemede istihdam
      // kaydı açılmıyor; zorunluluğu oraya da yazmak, ünvansız eski satırların
      // e-postasını düzeltmeyi bloke ederdi.
      const jobTitle = String(raw["job_title"] ?? "").trim();
      if (titleRequired && jobTitle.length === 0) {
        issues.push({
          path: ["job_title"],
          message: msg("people.form.error.job_title_required"),
        });
      }

      if (issues.length > 0) {
        return { success: false, error: { issues } };
      }
      return {
        success: true,
        data: {
          legal_first_name: firstName,
          legal_last_name: lastName,
          primary_work_email: email,
          start_date: startDate,
          job_title: jobTitle,
          employment_type: String(raw["employment_type"] ?? "").trim(),
          manager_id: String(raw["manager_id"] ?? "").trim(),
          department_id: String(raw["department_id"] ?? "").trim(),
          legal_entity_id: legalEntityId,
          org_dimension_values: collectDimensionValues(raw, dimensions),
        },
      };
    },
  };
}

/** Kiracının AKTİF boyut kataloğundan çizilecek tek alan. Değerler
 *  `entity_employee.org_dimension_values` haritasına slug ile yazılır. */
export interface OrgDimensionField {
  readonly slug: string;
  readonly label: string;
  readonly allowedValues?: readonly string[];
}

// Boyut alanları form gövdesinde düz anahtarlar olarak dolaşıyor; ön ek, sabit
// alanlarla çakışmayı imkânsız kılıyor ve toplama adımının hangi anahtarların
// boyut olduğunu tahmin etmesini gerektirmiyor.
const DIMENSION_FIELD_PREFIX = "org_dim__";

function collectDimensionValues(
  raw: Record<string, unknown>,
  dimensions: readonly OrgDimensionField[],
): Readonly<Record<string, string>> {
  const values: Record<string, string> = {};
  for (const dim of dimensions) {
    const value = String(raw[`${DIMENSION_FIELD_PREFIX}${dim.slug}`] ?? "").trim();
    // Boş bırakılan bir boyut haritaya yazılmıyor: uç boş dizeyi de bir değer
    // sayar ve "seçilmemiş" ile "boş seçilmiş" ayrımı kaybolurdu.
    if (value !== "") values[dim.slug] = value;
  }
  return values;
}

export interface EmployeeFormContentProps {
  /** "create" or "edit" — drives the page title + submit label. */
  readonly mode: "create" | "edit";
  /** Submit handler — the host wires this to the POST/PATCH mutation. */
  readonly onSubmit: (draft: EmployeeDraft) => void | Promise<void>;
  /**
   * Manager picker candidates (the existing employees list is the only ready
   * FE option-source). Empty value = no manager.
   */
  readonly managerOptions?: readonly ResourceFieldOption[];
  /**
   * Departman seçicisinin seçenekleri (E10): aktif departmanlar. Boşsa alan hiç
   * çizilmiyor, yani liste okunamayan biri için form hâlâ kaydedilebilir.
   */
  readonly departmentOptions?: readonly ResourceFieldOption[];
  /**
   * Şirket seçicisinin seçenekleri (SEM-6755). Kaynak `GET /v1/legal-entities`
   * ve o uç çağıranın hibesine daraltılmış durumda — yani buraya gelen küme
   * zaten yazılabilir şirketlerin kümesi. Boşsa alan hiç çizilmiyor.
   */
  readonly companyOptions?: readonly ResourceFieldOption[];
  /**
   * Kiracının aktif org boyutları (SEM-7205). Yalnız düzenlemede doluyor:
   * yaratma ucu `org_dimension_values` kabul etmiyor, o yüzden orada alanı
   * çizmek doldurulup sessizce düşen bir alan olurdu.
   */
  readonly dimensions?: readonly OrgDimensionField[];
  /** Edit-mode prefill — the existing row's values keyed by field name. */
  readonly defaults?: Partial<EmployeeDraft>;
}

export function EmployeeFormContent({
  mode,
  onSubmit,
  managerOptions = [],
  departmentOptions = [],
  companyOptions = [],
  dimensions = [],
  defaults = {},
}: EmployeeFormContentProps): React.ReactElement {
  const { t } = useTranslation();

  const employmentTypeOptions: readonly ResourceFieldOption[] = [
    { value: "", label: t("people.form.employment_type_none") },
    ...EMPLOYMENT_TYPES.map((type) => ({
      value: type,
      label: t(`people.employment_type.${type}`),
    })),
  ];

  const showCompany = companyOptions.length > 0;
  // Tek şirketli kiracıda seçici bir soru sormuyor, o yüzden tek seçenek
  // önden dolu geliyor: aksi hâlde kullanıcı tek cevabı olan bir alanı
  // doldurmaya zorlanır ve boş bırakırsa form zorunluluk hatası verir.
  // Boş dize "kayıtlı değer yok" demek, "değer boş" değil: şirketi olmayan
  // eski bir satır düzenlenirken de tek seçenek önden dolmalı.
  const companyDefault =
    defaults.legal_entity_id !== undefined && defaults.legal_entity_id !== ""
      ? defaults.legal_entity_id
      : companyOptions.length === 1
        ? companyOptions[0]?.value
        : undefined;

  const fields: readonly ResourceField[] = [
    {
      name: "legal_first_name",
      label: t("people.form.first_name"),
      type: "text",
      required: true,
      ...(defaults.legal_first_name !== undefined && {
        defaultValue: defaults.legal_first_name,
      }),
    },
    {
      name: "legal_last_name",
      label: t("people.form.last_name"),
      type: "text",
      required: true,
      ...(defaults.legal_last_name !== undefined && {
        defaultValue: defaults.legal_last_name,
      }),
    },
    {
      name: "primary_work_email",
      label: t("people.form.email"),
      type: "email",
      required: true,
      ...(defaults.primary_work_email !== undefined && {
        defaultValue: defaults.primary_work_email,
      }),
    },
    {
      name: "start_date",
      label: t("people.form.start_date"),
      type: "date",
      required: true,
      ...(defaults.start_date !== undefined && { defaultValue: defaults.start_date }),
    },
    ...(showCompany
      ? [
          {
            name: "legal_entity_id",
            label: t("people.form.company"),
            type: "select" as const,
            required: mode === "create",
            // Düzenlemede kilitli (MC-19b): şirket değiştirmek bir alan
            // düzenlemesi değil bir transfer geçişidir ve yama ucu artık bu
            // alanı reddediyor. Alan gizlenmiyor, çünkü kişinin hangi şirkette
            // olduğu formun okunması gereken bir bilgisi.
            disabled: mode === "edit",
            options: [
              { value: "", label: t("people.form.company_none") },
              ...companyOptions,
            ],
            ...(companyDefault !== undefined && { defaultValue: companyDefault }),
            ...(mode === "edit" && {
              help: t("people.form.company_locked_help"),
              // Yardım düğmesinin adı alan etiketiyle AYNI olamaz: iki öğe aynı
              // erişilebilir adı taşıyınca ekran okuyucu ikisini ayırt edemez.
              helpLabel: t("people.form.company_locked_label"),
            }),
          },
        ]
      : []),
    {
      name: "job_title",
      label: t("people.form.job_title"),
      type: "text",
      required: mode === "create",
      ...(defaults.job_title !== undefined && { defaultValue: defaults.job_title }),
    },
    {
      name: "employment_type",
      label: t("people.form.employment_type"),
      type: "select",
      options: employmentTypeOptions,
      ...(defaults.employment_type !== undefined && {
        defaultValue: defaults.employment_type,
      }),
    },
    ...(departmentOptions.length > 0
      ? [
          {
            name: "department_id",
            label: t("people.form.department"),
            type: "select" as const,
            // Uç `department_id`'yi atar ama null ile silmez, yani boş seçim
            // "kaldır" değil "dokunma" demektir. Departmanı zaten olan bir
            // kişide boş seçenek hiç sunulmuyor: sunulsa "departmanı kaldır"
            // gibi okunur ve kayıttan sonra hiçbir şey olmazdı.
            options: [
              ...(defaults.department_id !== undefined && defaults.department_id !== ""
                ? []
                : [{ value: "", label: t("people.form.department_none") }]),
              ...departmentOptions,
            ],
            ...(defaults.department_id !== undefined && { defaultValue: defaults.department_id }),
          },
        ]
      : []),
    {
      name: "manager_id",
      label: t("people.form.manager"),
      type: "select",
      options: [{ value: "", label: t("people.form.manager_none") }, ...managerOptions],
      ...(defaults.manager_id !== undefined && { defaultValue: defaults.manager_id }),
    },
    ...dimensions.map((dim): ResourceField => {
      const current = defaults.org_dimension_values?.[dim.slug];
      const base = {
        name: `${DIMENSION_FIELD_PREFIX}${dim.slug}`,
        label: dim.label,
        ...(current !== undefined && { defaultValue: current }),
      };
      // Değer kümesi ilan edilmişse seçici, değilse serbest metin: kapalı bir
      // kümeyi metin alanı olarak sunmak, ucun reddedeceği değeri yazdırırdı.
      return dim.allowedValues !== undefined && dim.allowedValues.length > 0
        ? {
            ...base,
            type: "select" as const,
            options: [
              { value: "", label: t("people.form.dimension_none") },
              ...dim.allowedValues.map((value) => ({ value, label: value })),
            ],
          }
        : { ...base, type: "text" as const };
    }),
  ];

  return (
    <div className="mx-auto max-w-[760px] space-y-5">
      <RouterLink
        to="/people"
        className="inline-flex items-center gap-1.5 font-sans text-[13px] text-[var(--fg-secondary)] no-underline hover:text-[var(--fg-primary)]"
      >
        <ChevronLeft size={14} aria-hidden="true" />
        {t("people.form.back")}
      </RouterLink>

      <ResourceForm<EmployeeDraft>
        className="space-y-5"
        fields={fields}
        validator={makeEmployeeValidator(
          t,
          showCompany && mode === "create",
          mode === "create",
          dimensions,
        )}
        onValidSubmit={onSubmit}
        submitLabel={
          mode === "edit" ? t("people.form.submit_edit") : t("people.form.submit_create")
        }
        collect={(formData): unknown => Object.fromEntries(formData.entries())}
        renderLayout={({ fields: fieldNodes, actions }) => (
          <>
            <PageHeader
              title={mode === "edit" ? t("people.form.title_edit") : t("people.form.title_create")}
              description={
                mode === "edit"
                  ? t("people.form.description_edit")
                  : t("people.form.description_create")
              }
              actions={actions}
            />
            <Card>
              <CardBody className="flex flex-col gap-3.5">{fieldNodes}</CardBody>
            </Card>
          </>
        )}
      />
    </div>
  );
}
